import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { MergeSuggestionDrawer } from '@/components/MergeSuggestionDrawer'
import { User, Check, X, Loader2, RefreshCw, ArrowRight, Camera, Clock, Columns2 } from 'lucide-react'

interface PersonMini {
  id: string
  name: string | null
  classificationName: string | null
  totalSightings: number
  thumbnailUrl: string | null
}

interface Suggestion {
  id: string
  sourcePersonId: string
  targetPersonId: string
  source: PersonMini
  target: PersonMini
  faceSim: number | null
  bodySim: number | null
  fusedScore: number | null
  sharedCameras: string[]
  timeOverlap: boolean
  gapMinutes: number | null
}

interface MergeSuggestionsTabProps {
  groupId: string
  canManagePersons: boolean
}

// The on-demand sweep runs asynchronously on the backend; refresh the list a
// little after triggering it so fresh suggestions appear without a manual reload.
const SCAN_REFRESH_DELAY_MS = 8000

function pct(v: number | null): string {
  return v == null ? '—' : `${Math.round(v * 100)}%`
}

function PersonThumb({ person }: { person: PersonMini }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-12 h-12 rounded-md overflow-hidden bg-muted flex items-center justify-center shrink-0">
        {person.thumbnailUrl ? (
          <img src={person.thumbnailUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <User className="w-6 h-6 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{person.name || '—'}</div>
        <div className="text-xs text-muted-foreground truncate">
          {person.classificationName || ''} · {person.totalSightings}
        </div>
      </div>
    </div>
  )
}

export function MergeSuggestionsTab({ groupId, canManagePersons }: MergeSuggestionsTabProps) {
  const { t } = useTranslation()
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(true)
  const [scanning, setScanning] = useState(false)
  const [actioningId, setActioningId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [compareId, setCompareId] = useState<string | null>(null)
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    setError(null)
    try {
      const fn = apiCallable('listMergeSuggestions')
      const res = await fn({ groupId })
      const data = res.data as { suggestions: Suggestion[] }
      setSuggestions(data.suggestions || [])
    } catch (e) {
      setError((e as Error)?.message || t('mergeSuggestions.loadError'))
    } finally {
      setLoading(false)
    }
  }, [groupId, t])

  useEffect(() => {
    load()
    return () => {
      // Group switch / unmount while the post-scan refresh timer is pending:
      // drop the timer AND clear the scanning flag, or Rescan stays disabled.
      if (scanTimerRef.current) {
        clearTimeout(scanTimerRef.current)
        scanTimerRef.current = null
      }
      setScanning(false)
    }
  }, [load])

  const runScan = async () => {
    setScanning(true)
    setError(null)
    try {
      const fn = apiCallable('runMergeSuggestions')
      await fn({ groupId })
      // The sweep is async on the backend — reload once it has had time to run.
      scanTimerRef.current = setTimeout(async () => {
        await load()
        setScanning(false)
      }, SCAN_REFRESH_DELAY_MS)
    } catch (e) {
      setError((e as Error)?.message || t('mergeSuggestions.scanError'))
      setScanning(false)
    }
  }

  // Bare actions: they throw so each caller (list row / compare drawer) can
  // surface the failure where the user is actually looking.
  const applySuggestion = useCallback(async (id: string) => {
    const fn = apiCallable('applyMergeSuggestion')
    await fn({ groupId, suggestionId: id })
    setSuggestions((s) => s.filter((x) => x.id !== id))
  }, [groupId])

  const dismissSuggestion = useCallback(async (id: string) => {
    const fn = apiCallable('dismissMergeSuggestion')
    await fn({ groupId, suggestionId: id })
    setSuggestions((s) => s.filter((x) => x.id !== id))
  }, [groupId])

  const apply = async (id: string) => {
    setActioningId(id)
    setError(null)
    try {
      await applySuggestion(id)
    } catch (e) {
      setError((e as Error)?.message || t('mergeSuggestions.mergeError'))
    } finally {
      setActioningId(null)
    }
  }

  const dismiss = async (id: string) => {
    setActioningId(id)
    setError(null)
    try {
      await dismissSuggestion(id)
    } catch (e) {
      setError((e as Error)?.message || t('mergeSuggestions.dismissError'))
    } finally {
      setActioningId(null)
    }
  }

  if (!canManagePersons) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          {t('mergeSuggestions.noPermission')}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{t('mergeSuggestions.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('mergeSuggestions.subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={runScan} disabled={scanning}>
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {scanning ? t('mergeSuggestions.scanning') : t('mergeSuggestions.rescan')}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="py-12 flex justify-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : suggestions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('mergeSuggestions.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s) => {
            const busy = actioningId === s.id
            return (
              <Card key={s.id}>
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Clicking the pair opens the side-by-side review panel —
                        thumbnails alone rarely settle a merge. */}
                    <button
                      type="button"
                      onClick={() => setCompareId(s.id)}
                      title={t('mergeSuggestions.compare.open')}
                      className="flex flex-1 min-w-0 flex-col gap-3 rounded-md p-1 -m-1 text-left transition hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex-row md:items-center"
                    >
                      {/* Persons (source merged INTO target) */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <PersonThumb person={s.source} />
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                        <PersonThumb person={s.target} />
                      </div>

                      {/* Evidence */}
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="px-2 py-1 rounded-md bg-primary/10 text-primary font-medium">
                          {t('mergeSuggestions.match')}: {pct(s.fusedScore)}
                        </span>
                        <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground">
                          {t('mergeSuggestions.face')} {pct(s.faceSim)}
                        </span>
                        {s.bodySim != null && s.bodySim > 0 && (
                          <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground">
                            {t('mergeSuggestions.body')} {pct(s.bodySim)}
                          </span>
                        )}
                        {s.sharedCameras?.length > 0 && (
                          <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground inline-flex items-center gap-1">
                            <Camera className="w-3 h-3" /> {s.sharedCameras.length}
                          </span>
                        )}
                        {s.timeOverlap && (
                          <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {t('mergeSuggestions.overlap')}
                          </span>
                        )}
                        <span className="px-2 py-1 rounded-md text-primary font-medium inline-flex items-center gap-1">
                          <Columns2 className="w-3 h-3" /> {t('mergeSuggestions.compare.open')}
                        </span>
                      </div>
                    </button>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" onClick={() => apply(s.id)} disabled={busy}>
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        {t('mergeSuggestions.merge')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => dismiss(s.id)} disabled={busy}>
                        <X className="w-4 h-4 mr-1" />
                        {t('mergeSuggestions.dismiss')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <MergeSuggestionDrawer
        open={compareId !== null}
        onOpenChange={(open) => { if (!open) setCompareId(null) }}
        groupId={groupId}
        suggestionId={compareId}
        onMerge={applySuggestion}
        onDismiss={dismissSuggestion}
      />
    </div>
  )
}
